import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import type { Dealership } from "@lumika/shared";
import { Badge, Button, PageError, PageLoading, cn } from "@lumika/ui";
import { adminApi } from "../api/admin";

const TABS = [
  { to: "overview", label: "Overview" },
  { to: "inbox", label: "Inbox" },
  { to: "contacts", label: "Contacts" },
  { to: "calendar", label: "Calendar" },
  { to: "inventory", label: "Inventory" },
  { to: "inventory-imports", label: "Inventory imports" },
  { to: "rooftops", label: "Rooftops" },
  { to: "users", label: "Users" },
  { to: "channels", label: "Channels" },
  { to: "integrations", label: "Integrations" },
  { to: "telephony", label: "Telephony" },
  { to: "ai", label: "AI" },
  { to: "billing", label: "Billing" },
  { to: "activity", label: "Activity" },
];

const STATUS_VARIANT: Record<Dealership["status"], "default" | "success" | "warning" | "secondary"> = {
  onboarding: "warning",
  pilot: "warning",
  active: "success",
  paused: "secondary",
  inactive: "secondary",
};

export function DealershipScope() {
  const { dealershipId } = useParams();
  const navigate = useNavigate();
  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  if (!dealershipId) return null;

  if (detailQuery.isLoading) {
    return <PageLoading message="Loading dealership…" />;
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <PageError
        title="Couldn't load this dealership"
        description={detailQuery.error?.message}
        action={
          <Button variant="outline" onClick={() => navigate("/dealerships")}>
            Back to list
          </Button>
        }
      />
    );
  }

  const detail = detailQuery.data;
  const rooftopCount = detail.rooftops.length;
  const userCount = detail.users.length;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-col gap-4 border-b bg-card px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Dealership
            </p>
            <h1 className="mt-1 truncate text-2xl font-semibold tracking-tight">
              {detail.name}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {rooftopCount} {rooftopCount === 1 ? "rooftop" : "rooftops"}
              <span className="mx-2 text-muted-foreground/40">·</span>
              {userCount} {userCount === 1 ? "user" : "users"}
            </p>
          </div>
          {detail.archivedAt && (
            <Badge variant="destructive" className="shrink-0">
              Archived
            </Badge>
          )}
          <Badge variant={STATUS_VARIANT[detail.status]} className="shrink-0">
            {detail.status}
          </Badge>
        </div>
        <nav className="-mx-6 flex gap-1 overflow-x-auto px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) =>
                cn(
                  "inline-flex min-h-touch shrink-0 items-center whitespace-nowrap rounded-md border border-transparent px-3 py-1.5 text-sm font-medium transition-colors",
                  isActive
                    ? "border-border bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <div className="min-w-0 flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
